import { useEffect, useState } from 'react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import {CheckCircle, XCircle, Eye} from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'

interface DealSubmission {
  _id: string
  title: string
  description: string
  code?: string
  discount: string
  expiryDate?: string
  affiliateLink: string
  brand: string
  category: string
  submittedBy: string
  submitterEmail: string
  status: 'pending' | 'approved' | 'rejected'
  moderatorNotes?: string
  createdAt: string
}

export default function AdminModeration() {
  const { user } = useAuth()
  const [submissions, setSubmissions] = useState<DealSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending')
  const [selectedSubmission, setSelectedSubmission] = useState<DealSubmission | null>(null)
  const [moderatorNotes, setModeratorNotes] = useState('')

  useEffect(() => {
    loadSubmissions()
  }, [filter])

  const loadSubmissions = async () => {
    try {
      setLoading(true)
      const filterQuery = filter === 'all' ? {} : { status: filter }
      
      const result = await lumi.entities.dealSubmissions.list({
        filter: filterQuery,
        sort: { createdAt: -1 }
      })
      
      setSubmissions(result.list || [])
    } catch (error) {
      console.error('Error loading submissions:', error)
      toast.error('Failed to load submissions')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (submission: DealSubmission) => {
    try {
      // Create coupon from submission
      await lumi.entities.coupons.create({
        title: submission.title,
        description: submission.description,
        code: submission.code,
        discount: submission.discount,
        expiryDate: submission.expiryDate,
        affiliateLink: submission.affiliateLink,
        brand: submission.brand,
        category: submission.category,
        verified: true,
        featured: false,
        active: true,
        clicks: 0,
        creator: user?.userId || 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // Update submission status
      await lumi.entities.dealSubmissions.update(submission._id, {
        status: 'approved',
        moderatorNotes,
        updatedAt: new Date().toISOString()
      })

      toast.success('Deal approved and published!')
      setSelectedSubmission(null)
      setModeratorNotes('')
      loadSubmissions()
    } catch (error) {
      console.error('Error approving submission:', error)
      toast.error('Failed to approve submission')
    }
  }

  const handleReject = async (submission: DealSubmission) => {
    if (!moderatorNotes.trim()) {
      toast.error('Please provide a reason for rejection')
      return
    }

    try {
      await lumi.entities.dealSubmissions.update(submission._id, {
        status: 'rejected',
        moderatorNotes,
        updatedAt: new Date().toISOString()
      })

      toast.success('Deal rejected')
      setSelectedSubmission(null)
      setModeratorNotes('')
      loadSubmissions()
    } catch (error) {
      console.error('Error rejecting submission:', error)
      toast.error('Failed to reject submission')
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Please sign in as admin</div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading submissions...</div>
      </div>
    )
  }

  return (
    <>
      <Toaster position="top-center" />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Deal Moderation</h1>

          {/* Filter Tabs */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="flex border-b">
              {(['all', 'pending', 'approved', 'rejected'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setFilter(tab)}
                  className={`px-6 py-3 font-medium capitalize ${
                    filter === tab
                      ? 'border-b-2 border-green-600 text-green-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab} ({submissions.filter(s => tab === 'all' || s.status === tab).length})
                </button>
              ))}
            </div>
          </div>

          {/* Submissions List */}
          <div className="space-y-4">
            {submissions.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <p className="text-gray-500">No submissions found</p>
              </div>
            )}

            {submissions.map((submission) => (
              <div key={submission._id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{submission.title}</h3>
                      <span
                        className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          submission.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : submission.status === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {submission.status.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">{submission.description}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Brand:</span>
                        <span className="ml-2 font-medium">{submission.brand}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Discount:</span>
                        <span className="ml-2 font-medium text-green-600">{submission.discount}</span>
                      </div>
                      {submission.code && (
                        <div>
                          <span className="text-gray-500">Code:</span>
                          <span className="ml-2 font-mono font-medium">{submission.code}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">Submitted by:</span>
                        <span className="ml-2 font-medium">{submission.submitterEmail}</span>
                      </div>
                    </div>
                  </div>
                  
                  {submission.status === 'pending' && (
                    <button
                      onClick={() => {
                        setSelectedSubmission(submission)
                        setModeratorNotes(submission.moderatorNotes || '')
                      }}
                      className="ml-4 p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {submission.moderatorNotes && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <strong>Notes:</strong> {submission.moderatorNotes}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Review Modal */}
          {selectedSubmission && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Review Submission</h2>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Title</label>
                    <p className="text-gray-900">{selectedSubmission.title}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Description</label>
                    <p className="text-gray-900">{selectedSubmission.description}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Brand</label>
                      <p className="text-gray-900">{selectedSubmission.brand}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Discount</label>
                      <p className="text-green-600 font-bold">{selectedSubmission.discount}</p>
                    </div>
                  </div>
                  {selectedSubmission.code && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Code</label>
                      <p className="font-mono font-bold">{selectedSubmission.code}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-700">Link</label>
                    <a
                      href={selectedSubmission.affiliateLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline break-all"
                    >
                      {selectedSubmission.affiliateLink}
                    </a>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Moderator Notes</label>
                    <textarea
                      value={moderatorNotes}
                      onChange={(e) => setModeratorNotes(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 mt-1"
                      rows={3}
                      placeholder="Add notes (required for rejection)..."
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleApprove(selectedSubmission)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Approve & Publish
                  </button>
                  <button
                    onClick={() => handleReject(selectedSubmission)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <XCircle className="w-5 h-5" />
                    Reject
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSubmission(null)
                      setModeratorNotes('')
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
